import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';

import { useAccount } from '../../../auth';
import { Model, ModelCollection } from '../../../data/api';
import { useCatalogType } from '../../../data/catalog';
import { useEntity } from '../../../data/flowEntities';
import { useBasicAPIMutation } from '../../../data/mutations';
import {
  getEntityType,
  getEntityCreateVerb,
  getPluralEntity,
} from '../../../utils/entities';
import { capitalize } from '../../../utils/strings';

import { BrowserTabTitle } from '../../BrowserTabTitle';
import ConnectorCard from './ConnectorCard';
import OAuthStep from './steps/OAuthStep';
import ObjectFieldTemplate from './steps/ObjectFieldTemplate';
import { breadcrumbMap } from '../../header/BreadcrumbNav';
import NameCard from './steps/NameCardStep';
import Typography from '@mui/material/Typography';
import { PageTitle } from '../../PageTitle';

export const WizardContext = React.createContext<React.Dispatch<any>>(
  () => null,
);

const FormStep = ({
  entity,
  step,
  stepNumber,
  activeStep,
  onNextClick,
  onBackClick,
}: any) => {
  const dispatch = React.useContext(WizardContext);

  const handleChange = ({ formData, errors }: any) => {
    const changes = {};
    for (const prop in step.schema.properties) {
      if (formData[prop]) {
        // @ts-ignore
        changes[prop] = formData[prop];
      }
    }
    // @ts-ignore
    dispatch({ type: 'update_entity_config', changes });

    if (errors && errors.length > 0) {
      // @ts-ignore
      dispatch({ type: 'change_validation_status', valid: false });
    } else {
      // @ts-ignore
      dispatch({ type: 'change_validation_status', valid: true });
    }
  };

  const uiSchema = step.ui_schema || {};
  uiSchema['ui:submitButtonOptions'] = {
    norender: true,
  };

  const configStep = entity.config?.[step.name];
  let configStepIsPopulated = false;
  if (typeof configStep === 'object') {
    configStepIsPopulated = Object.keys(configStep).length > 0;
  }
  if (typeof configStep === 'string' || Array.isArray(configStep)) {
    configStepIsPopulated = configStep.length > 0;
  }
  const isNotCurrentStep = activeStep !== stepNumber - 1;

  return (
    <ConnectorCard
      title={step.title}
      stepNumber={stepNumber}
      completed={configStepIsPopulated && isNotCurrentStep}
      active={activeStep === stepNumber - 1}
      disabled={activeStep !== stepNumber - 1}
      sx={{ marginBottom: '1em' }}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      renderChildren={(buttonsDisabled) => (
        <Form
          templates={{ ObjectFieldTemplate }}
          disabled={buttonsDisabled}
          liveValidate
          showErrorList={false}
          omitExtraData={false}
          liveOmit={false}
          validator={validator}
          schema={step.schema}
          uiSchema={uiSchema}
          formData={entity.config}
          onChange={handleChange}
        />
      )}
    />
  );
};

const WizardStep = ({
  catalogType,
  entityType,
  entity,
  step,
  stepNumber,
  activeStep,
  dispatch,
  onNextClick,
  onBackClick,
  wizardState,
}: any) => {
  const isLastStep =
    catalogType?.setup?.config_steps?.length > 0 &&
    catalogType.setup.config_steps[catalogType.setup.config_steps.length - 1]
      .title === step.title;

  if (step.type == 'name' || catalogType.setup.config_steps.length == 0) {
    return (
      <>
        {/* @ts-ignore */}
        <NameCard
          entityType={entityType}
          entity={entity}
          stepNumber={stepNumber}
          activeStep={activeStep}
          catalogType={catalogType}
          dispatch={dispatch}
          {...(!isLastStep && { onNextClick, onBackClick })}
          {...(isLastStep && { onBackClick })}
        />
      </>
    );
  }

  if (step.type == 'form') {
    return (
      <FormStep
        entityType={entityType}
        entity={entity}
        step={step}
        stepNumber={stepNumber}
        activeStep={activeStep}
        wizardState={wizardState}
        {...(!isLastStep && { onNextClick, onBackClick })}
        {...(isLastStep && { onBackClick })}
      />
    );
  }

  if (step.type == 'oauth') {
    return (
      <OAuthStep
        step={step}
        entityType={entityType}
        entity={entity}
        catalogType={catalogType}
        stepNumber={stepNumber}
        activeStep={activeStep}
        {...(!isLastStep && { onNextClick, onBackClick })}
        {...(isLastStep && { onBackClick })}
      />
    );
  }
  return <> </>;
};

const wizardReducer = (state: any, action: any) => {
  const newState = Object.assign({}, state);

  switch (action.type) {
    case 'set_entity':
      newState.entity = action.entity;
      newState.activeStepName = action.entity.setup_current_step;
      break;
    case 'change_active_step':
      newState.activeStepName = action.name;
      newState.entity.setup_current_step = action.name;
      break;
    case 'change_validation_status':
      newState.currentStepValid = action.valid;
      break;
    case 'change_entity_name':
      newState.entity.name = action.name;
      break;
    case 'update_entity_config':
      newState.entity.config = Object.assign(
        {},
        newState.entity.config,
        action.changes,
      );
      break;
    case 'finalize_entity':
      newState.entity.setup_current_step = null;
      newState.entity.enabled = true;
      break;
    default:
      throw new Error(`EntityWizard action type not supported: ${action.type}`);
  }

  return newState;
};

export const EntityWizard = () => {
  const { currentAccount } = useAccount();
  const navigate = useNavigate();
  const { pluralEntityType, catalogTypeName, entityName } = useParams();
  const entityType = getEntityType(pluralEntityType);
  const createVerb = getEntityCreateVerb(entityType);
  const catalogTypeQuery = useCatalogType(
    currentAccount.id,
    entityType,
    catalogTypeName,
  );
  const entityQuery = useEntity(currentAccount.id, entityType, entityName);
  const pluralEntity = getPluralEntity(entityType)('entityHook');

  const entityMutation = useBasicAPIMutation(currentAccount.id, pluralEntity);

  // redirect to /connections if the entity type is not navigable
  React.useEffect(() => {
    const navigablePaths = Object.entries(breadcrumbMap)
      // @ts-ignore
      .map(([item, config]) => config.path)
      .filter(Boolean);
    const urlPaths = window.location.pathname.split('/');
    if (
      urlPaths.includes('integrations') ||
      !navigablePaths.includes(pluralEntityType)
    ) {
      navigate(
        `/${currentAccount.name}/connections/${createVerb}/${catalogTypeName}${
          entityName ? '/' + entityName : ''
        }`,
        { replace: true },
      );
    }
  }, []);

  const [wizardState, dispatch] = React.useReducer(wizardReducer, {
    activeStepName: 'name',
    currentStepValid: false,
  });

  React.useEffect(() => {
    if (
      !entityQuery.isLoading &&
      (!wizardState.entity ||
        (wizardState.entity &&
          !wizardState.entity.id &&
          entityQuery.data &&
          // @ts-ignore
          entityQuery.data.id))
    ) {
      let entity;
      if (entityQuery.data) {
        entity = entityQuery.data;
      } else {
        entity = new Model(entityType, {
          type_name: catalogTypeName,
          enabled: false,
          config: {},
        });
      }

      dispatch({ type: 'set_entity', entity });
    }
  });

  const catalogType = catalogTypeQuery.data;
  let activeStepIndex = 0;

  React.useEffect(() => {
    if (
      catalogTypeQuery.isLoading == false &&
      entityQuery.isLoading == false &&
      // @ts-ignore
      catalogType?.setup?.config_steps &&
      // @ts-ignore
      catalogType.setup.config_steps.length > 0 &&
      createVerb === 'configure'
    ) {
      dispatch({
        type: 'change_active_step',
        // @ts-ignore
        name: catalogType.setup.config_steps[activeStepIndex].name,
      });
      dispatch({ type: 'change_validation_status', valid: true });
    }
  }, [activeStepIndex]);

  // TODO: handle errors
  const stepIsLoading =
    !wizardState.entity ||
    catalogTypeQuery.isLoading ||
    entityQuery.isLoading ||
    entityMutation.isPending;

  if (stepIsLoading) {
    return <CircularProgress />;
  }

  const steps =
    createVerb !== 'configure'
      ? [
          {
            type: 'name',
            title: 'Name ' + capitalize(entityType),
            name: 'name',
          },
        ]
      : [];

  let i = createVerb !== 'configure' ? 1 : 0;
  // @ts-ignore
  if (catalogType && catalogType.setup && catalogType?.setup?.config_steps) {
    // @ts-ignore
    for (const step of catalogType.setup.config_steps) {
      if (wizardState.activeStepName === (step.name || step.title)) {
        activeStepIndex = i;
      }
      steps.push(step);
      i++;
    }
  }

  const previousStep = async (currentStepIndex: number) => {
    const prevStepName = steps[currentStepIndex - 1].name;
    dispatch({ type: 'change_active_step', name: prevStepName });
    await entityMutation.mutateAsync(wizardState.entity);
  };

  const advanceStep = async (currentStepIndex: number) => {
    if (currentStepIndex === steps.length - 1) {
      dispatch({ type: 'finalize_entity' });
      await entityMutation.mutateAsync(wizardState.entity);
      navigate(`/${currentAccount.name}/${pluralEntityType}`);
    } else {
      const nextStepName = steps[currentStepIndex + 1].name;
      dispatch({ type: 'change_active_step', name: nextStepName });
      await entityMutation.mutateAsync(wizardState.entity);

      if (!entityName) {
        //TODO: replace url without page reload
        navigate(
          `/${currentAccount.name}/${pluralEntityType}/${createVerb}/${catalogTypeName}/${wizardState.entity.name}`,
        );
      }
    }
  };
  const isLastStep =
    // @ts-ignore
    catalogType?.setup?.config_steps?.length > 0 &&
    // @ts-ignore
    catalogType.setup.config_steps[catalogType.setup.config_steps.length - 1]
      .name === wizardState.activeStepName;
  let finishIsDisabled = true;
  if (createVerb === 'connect' && isLastStep) {
    finishIsDisabled = !wizardState.currentStepValid;
  }
  if (createVerb === 'configure') {
    finishIsDisabled = false;
  }

  return (
    <div>
      <BrowserTabTitle>
        {capitalize(createVerb) + ' ' + capitalize(entityType)}
      </BrowserTabTitle>
      <PageTitle />
      {/*  @ts-ignore */}
      <WizardContext.Provider value={dispatch}>
        <div
          style={{
            // width: '100%',
            width: '492px',
            height: 81,
            margin: '0 auto',
            backgroundColor: '#FAFAFA',
          }}
        >
          <Grid
            container
            style={{
              maxHeight: 81,
              margin: '0 auto',
              padding: '0.5em',
            }}
          >
            <Grid item xs={2}>
              <img
                // @ts-ignore
                src={catalogType.img_url}
                style={{
                  maxWidth: 65,
                  margin: '0 auto',
                  display: 'block',
                }}
              />
            </Grid>
            <Grid item xs={10}>
              <span
                style={{
                  fontFamily: 'Noto Sans,serif',
                  fontSize: '1em',
                  backgroundSize: 'contain',
                }}
              >
                {capitalize(createVerb)}{' '}
                {
                  // @ts-ignore
                  catalogType?.title
                }
              </span>

              <br />
              <span
                style={{
                  fontFamily: 'Source Serif Pro,serif',
                  fontSize: '2em',
                  color: wizardState.entity.name ? '#000' : '#979797',
                }}
              >
                {/* @ts-ignore */}
                {wizardState.entity.name || catalogType.title}
              </span>
            </Grid>
          </Grid>
        </div>
        <Grid
          container
          style={{ paddingBottom: '2em', maxWidth: 556, margin: '0 auto' }}
        >
          <Grid item xs={12} style={{ margin: '2em', marginBottom: '1em' }}>
            {steps?.length == 0 && (
              <Typography variant="body1" color="initial">
                There is no further configuration required.
              </Typography>
            )}
            {steps?.length > 0 &&
              steps.map((step, index) => (
                <WizardStep
                  catalogType={catalogType}
                  entityType={entityType}
                  entity={wizardState.entity}
                  step={step}
                  stepNumber={index + 1}
                  wizardState={wizardState}
                  activeStep={activeStepIndex}
                  dispatch={dispatch}
                  onBackClick={() => previousStep(activeStepIndex)}
                  onNextClick={() => advanceStep(activeStepIndex)}
                />
              ))}
          </Grid>
          {/* save button */}
          {/* @ts-ignore */}
          {catalogType?.setup?.config_steps?.length > 0 && (
            <Grid
              item
              xs={12}
              style={{ marginLeft: '2em', marginRight: '2em' }}
            >
              <div style={{ textAlign: 'right' }}>
                <Button
                  variant="contained"
                  fullWidth={true}
                  disabled={finishIsDisabled}
                  onClick={() => advanceStep(activeStepIndex)}
                >
                  Finish
                </Button>
              </div>
            </Grid>
          )}
        </Grid>
      </WizardContext.Provider>
    </div>
  );
};
